import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowUturnLeftIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { objectStorageService } from '../../services/objectStorageService';

interface Props {
  projectId: string;
  bucketId: string;
  objectKey: string;
  onClose: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function VersionHistory({ projectId, bucketId, objectKey, onClose }: Props) {
  const queryClient = useQueryClient();

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['object-versions', projectId, bucketId, objectKey],
    queryFn: () => objectStorageService.listObjectVersions(projectId, bucketId, objectKey),
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) =>
      objectStorageService.restoreObjectVersion(projectId, bucketId, objectKey, versionId),
    onSuccess: () => {
      toast.success('Version restored');
      queryClient.invalidateQueries({ queryKey: ['object-versions', projectId, bucketId, objectKey] });
      queryClient.invalidateQueries({ queryKey: ['objects', projectId, bucketId] });
    },
    onError: () => toast.error('Failed to restore version'),
  });

  const deleteMutation = useMutation({
    mutationFn: (versionId: string) =>
      objectStorageService.deleteObjectVersion(projectId, bucketId, objectKey, versionId),
    onSuccess: () => {
      toast.success('Version deleted');
      queryClient.invalidateQueries({ queryKey: ['object-versions', projectId, bucketId, objectKey] });
    },
    onError: () => toast.error('Failed to delete version'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Version History</h3>
            <p className="text-xs text-gray-400 mt-0.5 font-mono">{objectKey}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-700 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : versions.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">No versions found</p>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.version_id} className="flex items-center justify-between bg-gray-900/60 rounded-lg px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-300 truncate max-w-48">{v.version_id}</span>
                      {v.is_latest && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded font-medium">
                          Latest
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-500">{new Date(v.last_modified).toLocaleString()}</span>
                      <span className="text-xs text-gray-500">{formatBytes(v.size)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    {!v.is_latest && (
                      <button
                        onClick={() => restoreMutation.mutate(v.version_id)}
                        disabled={restoreMutation.isPending}
                        title="Restore this version"
                        className="p-1.5 text-primary-400 hover:text-primary-300 hover:bg-primary-500/10 rounded transition-colors disabled:opacity-50"
                      >
                        <ArrowUturnLeftIcon className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteMutation.mutate(v.version_id)}
                      disabled={deleteMutation.isPending}
                      title="Delete this version"
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
